import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Loader2, RotateCcw, X } from 'lucide-react';
import { adminRefundsAPI } from '../../services/api';
import { formatMoney } from '../../utils/format';

/**
 * Refund a payment (Phase 7d) — a deposit payment or a Pay Now payment.
 *
 * Two routes, chosen explicitly:
 *   - **Refund to the customer**: sent back through the gateway that took the
 *     payment. On Authorize.net an unsettled payment is voided instead (the
 *     server decides and says which happened).
 *   - **Record a refund made outside the portal**: the old behaviour, for a
 *     refund already made by cheque or in the merchant interface. Nothing is
 *     sent; it is audited as such.
 *
 * Two steps in the page itself — the form, then a confirmation showing the
 * amount, the card or account, the gateway and what will happen — because a
 * refund cannot be taken back. One request id per opening of the dialog, so
 * a double-click on Confirm is one refund, not two.
 *
 * When the payment was applied to ConnectWise invoices, the admin is asked
 * here whether to update the ConnectWise invoice payment too (ticked by
 * default). Under the box, per invoice, "ConnectWise payment: $X -> $Y" — the
 * numbers come from the server's preview, never from arithmetic here. The
 * confirmation step repeats the choice.
 */
function CwInvoiceLines({ invoices }) {
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-neutral-600 dark:text-neutral-300">
      {invoices.map((inv) => (
        <li key={inv.invoiceId} className="flex justify-between gap-3">
          <span>Invoice {inv.invoiceNumber || `#${inv.invoiceId}`}{inv.closed ? ' (closed)' : ''}</span>
          <span className="text-right">
            ConnectWise payment: {formatMoney(inv.fromTotal)} → {inv.removed ? 'removed' : formatMoney(inv.toTotal)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function newNonce() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function RefundPaymentModal({ source, paymentId, onClose, onDone }) {
  const nonce = useMemo(() => newNonce(), []);
  const [mode, setMode] = useState('gateway');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('form'); // form | confirm | done
  const [result, setResult] = useState(null);
  const [updateCw, setUpdateCw] = useState(true);
  const [cwAmount, setCwAmount] = useState('');

  const { data: preview, isLoading, error: previewError } = useQuery({
    queryKey: ['refund-preview', source, paymentId],
    queryFn: () => adminRefundsAPI.preview(source, paymentId).then((r) => r.data),
    retry: false,
  });

  useEffect(() => {
    if (!preview) return;
    setAmount(String(preview.refundable ?? ''));
    if (!preview.canRefundThroughGateway) setMode('external');
  }, [preview]);

  // The ConnectWise preview depends on the amount; wait for typing to settle.
  useEffect(() => {
    const t = setTimeout(() => setCwAmount(amount), 400);
    return () => clearTimeout(t);
  }, [amount]);
  const cwValue = parseFloat(cwAmount);
  const { data: cwPreview, isFetching: cwLoading } = useQuery({
    queryKey: ['refund-cw-preview', source, paymentId, cwValue],
    queryFn: () => adminRefundsAPI.previewConnectWise(source, paymentId, cwValue).then((r) => r.data),
    enabled: Boolean(preview) && Number.isFinite(cwValue) && cwValue > 0 && cwValue <= (preview?.refundable ?? 0) + 0.0001,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const cwApplies = Boolean(cwPreview?.appliesToConnectWise);
  const cwInvoices = cwPreview?.invoices || [];

  const refund = useMutation({
    mutationFn: () =>
      adminRefundsAPI
        .refund(source, paymentId, { amount: parseFloat(amount), reason: reason || undefined, mode, nonce, connectwise: cwApplies && updateCw })
        .then((r) => r.data),
    onSuccess: (data) => {
      setResult(data);
      setStep('done');
      onDone?.(data);
    },
  });

  const value = parseFloat(amount);
  const valid = Number.isFinite(value) && value > 0 && preview && value <= preview.refundable + 0.0001;
  const method = preview?.last4 ? `${preview.methodType || 'Card'} ending ${preview.last4}` : 'The original payment method';
  const busy = refund.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-labelledby="refund-title">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 id="refund-title" className="text-lg font-semibold dark:text-white">
            {step === 'done' ? 'Refund complete' : step === 'confirm' ? 'Confirm refund' : 'Refund payment'}
          </h3>
          <button onClick={onClose} disabled={busy} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading payment…
            </div>
          )}
          {previewError && (
            <p className="text-sm text-red-600 dark:text-red-400">{previewError.response?.data?.error || 'Could not load this payment.'}</p>
          )}

          {preview && step === 'form' && (
            <>
              <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between"><span className="text-neutral-500 dark:text-neutral-400">Paid</span><span className="dark:text-white">{formatMoney(preview.amount)}</span></div>
                {preview.refunded > 0 && (
                  <div className="flex justify-between"><span className="text-neutral-500 dark:text-neutral-400">Already refunded</span><span className="dark:text-white">{formatMoney(preview.refunded)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-neutral-500 dark:text-neutral-400">Left to refund</span><span className="font-medium dark:text-white">{formatMoney(preview.refundable)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500 dark:text-neutral-400">Method</span><span className="dark:text-white">{method}</span></div>
                {preview.gatewayLabel && (
                  <div className="flex justify-between"><span className="text-neutral-500 dark:text-neutral-400">Gateway</span><span className="dark:text-white">{preview.gatewayLabel}</span></div>
                )}
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">How</legend>
                <label className={`flex gap-2 items-start text-sm ${preview.canRefundThroughGateway ? '' : 'opacity-50'}`}>
                  <input type="radio" name="refund-mode" value="gateway" checked={mode === 'gateway'} disabled={!preview.canRefundThroughGateway} onChange={() => setMode('gateway')} className="mt-1" />
                  <span className="dark:text-neutral-200">
                    Refund to the customer through {preview.gatewayLabel || 'the gateway'}
                    {!preview.canRefundThroughGateway && preview.gatewayUnavailableReason && (
                      <span className="block text-xs text-neutral-500 dark:text-neutral-400">{preview.gatewayUnavailableReason}</span>
                    )}
                  </span>
                </label>
                <label className="flex gap-2 items-start text-sm">
                  <input type="radio" name="refund-mode" value="external" checked={mode === 'external'} disabled={!preview.canRecordExternal} onChange={() => setMode('external')} className="mt-1" />
                  <span className="dark:text-neutral-200">
                    Record a refund made outside the portal
                    <span className="block text-xs text-neutral-500 dark:text-neutral-400">Already refunded by cheque or in the merchant dashboard. No money is sent.</span>
                  </span>
                </label>
              </fieldset>

              <div>
                <label htmlFor="refund-amount" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Amount</label>
                <input id="refund-amount" type="number" step="0.01" min="0.01" max={preview.refundable} value={amount} onChange={(e) => setAmount(e.target.value)} className="input w-full" />
                {amount && !valid && <p className="text-xs text-red-600 dark:text-red-400 mt-1">Enter an amount up to {formatMoney(preview.refundable)}.</p>}
              </div>
              <div>
                <label htmlFor="refund-reason" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Reason (optional)</label>
                <textarea id="refund-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="input w-full" placeholder={mode === 'external' ? 'e.g. Cheque 1042' : 'e.g. Order cancelled'} />
              </div>

              {cwApplies && (
                <div className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-700">
                  <label className="flex gap-2 items-start text-sm">
                    <input type="checkbox" checked={updateCw} onChange={(e) => setUpdateCw(e.target.checked)} className="mt-1" />
                    <span className="dark:text-neutral-200">
                      Also update the ConnectWise invoice payment
                      {cwLoading && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
                    </span>
                  </label>
                  <CwInvoiceLines invoices={cwInvoices} />
                  {cwPreview?.order && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{cwPreview.order}</p>}
                  {cwPreview?.unallocated > 0 && (
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      {formatMoney(cwPreview.unallocated)} of this refund was never applied to an invoice (a surcharge, for example), so no ConnectWise payment changes for it.
                    </p>
                  )}
                  {cwInvoices.some((i) => i.closed) && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">A closed invoice cannot have its payment changed through ConnectWise&rsquo;s API; adjust it in ConnectWise.</p>
                  )}
                  {cwInvoices.some((i) => i.mismatch) && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">ConnectWise currently holds a different amount from the portal&rsquo;s records; the payment will be rewritten to the portal&rsquo;s total.</p>
                  )}
                  {!updateCw && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">The refund will be recorded as not reflected in ConnectWise. You can update ConnectWise later from the invoice.</p>}
                </div>
              )}
              {!cwApplies && cwPreview?.note && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">{cwPreview.note}</p>
              )}
            </>
          )}

          {preview && step === 'confirm' && (
            <>
              <div className={`flex gap-3 p-4 rounded-lg ${mode === 'gateway' ? 'bg-red-50 dark:bg-red-900/30' : 'bg-amber-50 dark:bg-amber-900/30'}`}>
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${mode === 'gateway' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                <p className={`text-sm ${mode === 'gateway' ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'}`}>
                  {mode === 'gateway'
                    ? 'This sends money back to the customer. It cannot be undone.'
                    : 'This only records a refund. No money is sent by the portal.'}
                </p>
              </div>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">Amount</dt><dd className="font-semibold dark:text-white">{formatMoney(value)}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">To</dt><dd className="dark:text-white">{method}</dd></div>
                <div className="flex justify-between"><dt className="text-neutral-500 dark:text-neutral-400">Through</dt><dd className="dark:text-white">{mode === 'gateway' ? preview.gatewayLabel : 'Recorded only'}</dd></div>
                {reason && <div className="flex justify-between gap-4"><dt className="text-neutral-500 dark:text-neutral-400">Reason</dt><dd className="dark:text-white text-right">{reason}</dd></div>}
                {cwApplies && (
                  <div className="flex justify-between gap-4"><dt className="text-neutral-500 dark:text-neutral-400">ConnectWise</dt><dd className="dark:text-white text-right">{updateCw ? 'Invoice payment will be updated' : 'Invoice payment will not be changed'}</dd></div>
                )}
              </dl>
              {cwApplies && updateCw && <CwInvoiceLines invoices={cwInvoices} />}
              {mode === 'gateway' && preview.gateway === 'authorizenet' && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  If this payment has not settled yet it is voided instead, and only the whole payment can be voided.
                </p>
              )}
              {refund.error && (
                <p className="text-sm text-red-600 dark:text-red-400">{refund.error.response?.data?.error || 'The refund did not go through.'}</p>
              )}
            </>
          )}

          {step === 'done' && result && (
            <div className="flex gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/30">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-800 dark:text-green-200">
                {result.kind === 'void'
                  ? `The payment had not settled, so it was voided: ${formatMoney(result.amount)} will not be taken from the customer.`
                  : result.kind === 'external'
                    ? `Recorded a refund of ${formatMoney(result.amount)} made outside the portal.`
                    : `Refunded ${formatMoney(result.amount)} to the customer.`}
                {result.refundId ? ` Reference ${result.refundId}.` : ''}
                {result.replay ? ' (This refund had already been made; nothing was sent twice.)' : ''}
              </p>
            </div>
          )}
          {step === 'done' && result?.connectwise && (
            <p className={`text-sm ${result.connectwise.status === 'reflected' ? 'text-green-700 dark:text-green-300' : result.connectwise.status === 'not_reflected' ? 'text-neutral-600 dark:text-neutral-300' : 'text-amber-700 dark:text-amber-300'}`}>
              {result.connectwise.status === 'reflected' && 'The ConnectWise invoice payment was updated.'}
              {result.connectwise.status === 'not_reflected' && (result.connectwise.error || 'The ConnectWise invoice payment was not changed.')}
              {result.connectwise.status === 'failed' && `The refund went through, but ConnectWise was not updated: ${result.connectwise.error || 'unknown error'}. Use Retry ConnectWise update on the invoice.`}
              {result.connectwise.status === 'not_allowed_closed' && 'The invoice is closed in ConnectWise, which refuses payment changes on it. Adjust its payment in ConnectWise.'}
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3">
          {step === 'form' && (
            <>
              <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
              <button onClick={() => setStep('confirm')} disabled={!valid || cwAmount !== amount || cwLoading} className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> Review refund
              </button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <button onClick={() => setStep('form')} disabled={busy} className="btn-secondary btn-sm">Back</button>
              <button onClick={() => refund.mutate()} disabled={busy} className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : mode === 'gateway' ? `Refund ${formatMoney(value)}` : `Record ${formatMoney(value)}`}
              </button>
            </>
          )}
          {step === 'done' && <button onClick={onClose} className="btn-primary btn-sm">Close</button>}
        </div>
      </div>
    </div>
  );
}
